/**
 * Document API Validators
 *
 * Validators for document-related API requests and responses
 *
 * @module validators/document
 */

import { z } from 'zod';
import type { IndexDocumentRequest } from '../contracts/rest-api.contract';

/**
 * Validates IndexDocumentRequest input
 *
 * @param data Unknown data to validate
 * @returns Validated IndexDocumentRequest
 * @throws ZodError if validation fails
 */
export const IndexDocumentRequestValidator = z.object({
  knowledge_base_id: z.string().min(1),
  title: z.string().min(1).max(255),
  content: z.string().min(1),
  metadata: z.record(z.string(), z.string()).optional(),
  content_type: z.enum(['TEXT', 'HTML', 'MARKDOWN', 'PDF', 'JSON']).optional(),
});

export function validateIndexDocumentRequest(data: unknown): IndexDocumentRequest {
  return IndexDocumentRequestValidator.parse(data);
}

/**
 * Type guard for IndexDocumentRequest
 *
 * @param data Unknown data to check
 * @returns true if data is IndexDocumentRequest
 */
export function isIndexDocumentRequest(data: unknown): data is IndexDocumentRequest {
  try {
    IndexDocumentRequestValidator.parse(data);
    return true;
  } catch {
    return false;
  }
}

/**
 * Safe parser that returns validation error details
 *
 * @param data Unknown data to parse
 * @returns { success: true; data } or { success: false; error }
 */
export function safeParseIndexDocumentRequest(
  data: unknown,
): { success: true; data: IndexDocumentRequest } | { success: false; error: z.ZodError } {
  const result = IndexDocumentRequestValidator.safeParse(data);
  return result;
}
